var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { InlineNotification } from '@ui';
import { ConnectCard } from '../ConnectCard';
import { EquipmentMappingCard } from '../EquipmentMappingCard';
import { JobFieldsCard } from '../JobFieldsCard';
import { PullCard } from '../PullCard';
import { RecentFailuresCard } from '../RecentFailuresCard';
import { SetupResults } from '../SetupResults';
import { StatusCard } from '../StatusCard';
import { connectCredentials, disconnectCredentials, rerunChecklist, startBulkPull } from '../shared/api';
import { translatorFor } from '../shared/i18n';
import { useLazyResource } from '../shared/useLazyResource';
import { usePullStatusPolling } from '../shared/usePullStatusPolling';
import { errorResponse } from '../shared/types';
import './ServiceTitanSettingsStyles.scss';
const t = translatorFor('page');
export function ServiceTitanSettings({ credentialStatus, pullSummary: initialPullSummary, bulkPullRunning, equipmentTypeMap: initialEquipmentTypeMap, ignoredEquipmentTypes, deviceTypeOptions, deviceSubtypeOptions, urls }) {
    const [status, setStatus] = useState(credentialStatus);
    const [pullSummary, setPullSummary] = useState(initialPullSummary);
    const [running, setRunning] = useState(bulkPullRunning);
    const [equipmentTypeMap, setEquipmentTypeMap] = useState(initialEquipmentTypeMap);
    const [ignoredTypes, setIgnoredTypes] = useState(ignoredEquipmentTypes);
    const checklist = useLazyResource(() => rerunChecklist(urls));
    const verification = checklist.data;
    const setVerification = checklist.setData;
    const [showSetup, setShowSetup] = useState(false);
    const [setupExpanded, setSetupExpanded] = useState(true);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState(null);
    usePullStatusPolling({
        urls,
        running,
        onStatus: (pullStatus) => {
            setPullSummary(pullStatus.pullSummary);
            setRunning(pullStatus.bulkPullRunning);
            if (!pullStatus.bulkPullRunning)
                setShowSetup(false);
        }
    });
    const connect = (payload) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const response = yield connectCredentials(status.connected ? urls.reconnect : urls.connect, payload);
        setStatus(response.credentialStatus);
        setVerification(response.verification);
        setSetupExpanded(true);
        setShowSetup(true);
        if ((_a = response.discovery) === null || _a === void 0 ? void 0 : _a.equipmentConfig)
            setEquipmentTypeMap((_b = response.discovery.equipmentConfig.equipmentTypeMap) !== null && _b !== void 0 ? _b : []);
        return response;
    });
    const disconnect = () => __awaiter(this, void 0, void 0, function* () {
        setStatus(yield disconnectCredentials(urls.disconnect));
        setVerification(null);
        setShowSetup(false);
    });
    const recheck = () => __awaiter(this, void 0, void 0, function* () {
        setVerification(yield rerunChecklist(urls));
    });
    const handlePullStarted = (summary) => {
        setPullSummary(summary);
        setRunning(true);
        setShowSetup(false);
    };
    const pullFromSetup = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setPulling(true);
        setPullError(null);
        try {
            handlePullStarted(yield startBulkPull(urls, { includeEquipment: true, includeContacts: true }));
        }
        catch (err) {
            setPullError((_b = (_a = errorResponse(err)) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : t('pull_failed'));
        }
        finally {
            setPulling(false);
        }
    });
    if (!status.connected) {
        return (React.createElement("div", { className: "service-titan-settings" },
            React.createElement(ConnectCard, { onConnect: connect })));
    }
    const setupVisible = showSetup && verification !== null && !running;
    return (React.createElement("div", { className: "service-titan-settings" },
        React.createElement(StatusCard, { credentialStatus: status, onReplaceSecret: connect, onDisconnect: disconnect }),
        setupVisible && verification ? (React.createElement(React.Fragment, null,
            React.createElement(SetupResults, { verification: verification, urls: urls, equipmentTypeMap: equipmentTypeMap, deviceTypeOptions: deviceTypeOptions, pulling: pulling, pullDisabled: running, onRecheck: recheck, onEquipmentTypeMapChange: setEquipmentTypeMap, onEquipmentTypeIgnored: (type) => setIgnoredTypes((previous) => previous.some((row) => row.stEquipmentTypeId === type.stEquipmentTypeId)
                    ? previous
                    : [...previous, type]), onExpandedChange: setSetupExpanded, onPull: () => {
                    void pullFromSetup();
                } }),
            pullError && React.createElement(InlineNotification, { variant: "error" }, pullError))) : (React.createElement(React.Fragment, null,
            React.createElement(PullCard, { urls: urls, pullSummary: pullSummary, running: running, onPullSummaryChange: setPullSummary, onPullStarted: handlePullStarted }),
            React.createElement(RecentFailuresCard, { urls: urls }))),
        React.createElement(EquipmentMappingCard, { urls: urls, checklist: checklist, equipmentTypeMap: equipmentTypeMap, ignoredTypes: ignoredTypes, deviceTypeOptions: deviceTypeOptions, deviceSubtypeOptions: deviceSubtypeOptions, onEquipmentTypeMapChange: setEquipmentTypeMap, onIgnoredTypesChange: setIgnoredTypes, hideUnmappedWarning: setupVisible && setupExpanded }),
        React.createElement(JobFieldsCard, { checklist: checklist })));
}
export default ServiceTitanSettings;
